import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmar } from '../components/confirmacao';
import apiService from '../services/apiService';
import { empresaInicial, lembrarEmpresa, comEmpresa } from '../components/empresaLembrada';
import { criarEscala, empilharFaixas, marcasDoEixo, posicaoDeHoje } from '../components/reguaGeometria';
import './Turmas.css';

/**
 * Turmas (§9.4). A turma é o que dá DATA ao programa: programa é template, turma é a execução.
 *
 * `anchor` é a decisão que mais confunde e por isso está explicada na tela:
 *   - 'cohort' = coorte fechada: todos começam na mesma data (a da turma).
 *   - 'member' = turma perene: cada um começa quando entra. O CS cria "Onboarding — Loja X" uma
 *     vez e só vai adicionando gente.
 *
 * "Loja de origem" e não "loja da turma" (§9.8): é onde a turma é coordenada, não uma restrição
 * sobre os membros. O rótulo é o que impede alguém de ler o campo como garantia — um membro pode
 * trocar de loja e continuar na turma, porque a fase é do programa.
 */

const brDate = (iso) => {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

// Quantas trilhas reais distintas desenhar por turma antes de resumir em "+N".
const MAX_TRILHAS_REAIS = 3;

/** Uma barra de fase na régua. `tipo`: 'real' | 'saiu' | 'planejada' (só muda o estilo). */
const barraDeFase = (f, tipo, rotulo) => (
    <div key={`${f.phase_id}-${f.from}`}
        className={`turmas-fase ${tipo}${f.locks_scoreboard ? ' trava' : ''}${f.semFim ? ' sem-fim' : ''}`}
        style={{ left: `${f.left}%`, width: `${Math.max(1.2, f.right - f.left)}%` }}
        title={`${rotulo}\n${f.sort_order}. ${f.name}\n${brDate(f.from)} → ${f.to ? brDate(f.to) : 'sem fim definido'}${f.duration_days ? `\n${f.duration_days} dias` : ''}${f.locks_scoreboard ? '\nSai do ranking nesta fase' : ''}`}>
        <span>{f.sort_order}. {f.name}{f.locks_scoreboard ? ' 🔒' : ''}</span>
    </div>
);

const emptyForm = { program_id: '', store_id: '', name: '', date_start: '', anchor: 'cohort' };

function Turmas({ user }) {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 5000);
    }, []);

    const [companies, setCompanies] = useState([]);
    const [subdomain, setSubdomain] = useState(() => empresaInicial(params.get('subdomain')));
    const [stores, setStores] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [cohorts, setCohorts] = useState([]);
    const [loading, setLoading] = useState(false);
    // Duas visões da MESMA informação, não duas telas: "Lista" responde "quais turmas existem" e
    // "Régua" responde "quando cada loja tem programa rodando — e quais lojas não têm nenhum".
    const [visao, setVisao] = useState('lista');
    const [regua, setRegua] = useState(null);
    const [carregandoRegua, setCarregandoRegua] = useState(false);
    // Recarrega a régua depois de alinhar uma turma, sem trocar de visão.
    const [reguaVersao, setReguaVersao] = useState(0);
    const [alinhando, setAlinhando] = useState(null);
    const [formOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const lista = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(lista) ? lista : []);
                // Só cai na primeira empresa se não houver nada lembrado nem na URL: senão o
                // voltar perderia o contexto do CS.
                setSubdomain((atual) => atual || (Array.isArray(lista) && lista.length ? lista[0].subdomain : ''));
            } catch (e) { notify('Erro ao carregar empresas', 'error'); }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!subdomain) return;
        lembrarEmpresa(subdomain);
        setCohorts([]); setFormOpen(false);
        (async () => {
            try {
                const [st, pr] = await Promise.all([
                    apiService.promptEditorGetStores(subdomain),
                    apiService.phaseProgramsList({ subdomain }),
                ]);
                setStores(Array.isArray(st) ? st : []);
                setPrograms(pr?.data?.programs || []);
            } catch (e) { notify('Erro ao carregar lojas/programas', 'error'); }
        })();
    }, [subdomain, notify]);

    const carregar = useCallback(async () => {
        if (!subdomain) return;
        setLoading(true);
        try {
            const r = await apiService.phaseCohortsList({ subdomain });
            setCohorts(r?.data?.cohorts || []);
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao carregar turmas', 'error');
        } finally { setLoading(false); }
    }, [subdomain, notify]);

    useEffect(() => { carregar(); }, [carregar]);

    const salvar = async () => {
        if (!form.program_id || !form.store_id || !form.name.trim() || !form.date_start) {
            return notify('Preencha programa, loja de origem, nome e data de início', 'error');
        }
        setSaving(true);
        try {
            await apiService.phaseCohortSave({
                subdomain,
                program_id: Number(form.program_id),
                store_id: Number(form.store_id),
                name: form.name.trim(),
                date_start: form.date_start,
                anchor: form.anchor,
            });
            notify('Turma criada', 'success');
            setForm(emptyForm); setFormOpen(false);
            carregar();
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao criar turma', 'error');
        } finally { setSaving(false); }
    };

    // A régua é buscada só quando a visão é pedida — é uma query por empresa, mas não faz sentido
    // pagá-la para quem só quer a lista.
    useEffect(() => {
        if (visao !== 'regua' || !subdomain) return;
        let vivo = true;
        setCarregandoRegua(true);
        (async () => {
            try {
                const r = await apiService.phaseStoresTimeline({ subdomain });
                if (vivo) setRegua(r?.data || null);
            } catch (e) {
                if (vivo) { setRegua(null); notify('Erro ao carregar a régua de fases', 'error'); }
            } finally {
                if (vivo) setCarregandoRegua(false);
            }
        })();
        return () => { vivo = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visao, subdomain, reguaVersao]);

    // Planejado := real. Prévia primeiro (a API não grava nada em dry_run), confirmação, e só então
    // grava. Só DURAÇÕES da turma mudam: trilha, selo e datas dos membros ficam como estão.
    const alinharPlano = async (t) => {
        setAlinhando(t.cohort_id);
        try {
            const previa = (await apiService.phaseAlignCohortPlan({ subdomain, cohort_id: t.cohort_id }))?.data;
            if (!previa?.alinhado) {
                notify(previa?.motivo || 'Esta turma não pode ser alinhada.', 'error');
                return;
            }
            if (!previa.mudou) {
                notify('O planejado desta turma já é igual ao real.', 'info');
                return;
            }
            const nomes = Object.fromEntries((t.planned || []).map((f) => [String(f.sort_order), f.name]));
            (t.effective?.[0]?.phases || []).forEach((f) => { nomes[String(f.sort_order)] = f.name; });
            const ordens = Object.keys({ ...previa.antes, ...previa.depois }).sort((a, b) => Number(a) - Number(b));
            const ok = await confirmar({
                titulo: `Alinhar o planejado de "${t.name}" ao real?`,
                rotuloConfirmar: 'Alinhar',
                corpo: (
                    <>
                        <ul className="dlg-lista">
                            {ordens.map((so) => {
                                const de = previa.antes[so] ?? (t.planned || []).find((f) => String(f.sort_order) === so)?.duration_days;
                                const para = previa.depois[so];
                                return (
                                    <li key={so}>
                                        {so}. <strong>{nomes[so] || 'fase'}</strong>: {de ?? 'programa'} → {para === 0 ? 'pulada' : `${para} dias`}
                                    </li>
                                );
                            })}
                        </ul>
                        {previa.inicio_diverge && (
                            <p className="dlg-alerta">
                                A turma começa em {brDate(previa.inicio_turma)} e a trilha real em {brDate(previa.inicio_real)}. O início <strong>não</strong> é alterado.
                            </p>
                        )}
                        <p className="dlg-nota">Muda só as durações planejadas desta turma. Nenhuma data, trilha ou atendimento é alterado.</p>
                    </>
                ),
            });
            if (!ok) return;
            await apiService.phaseAlignCohortPlan({ subdomain, cohort_id: t.cohort_id, dry_run: false });
            notify('Planejado da turma alinhado ao real.', 'success');
            setReguaVersao((v) => v + 1);
        } catch (e) {
            notify(e?.response?.data?.detail || 'Erro ao alinhar o planejado da turma', 'error');
        } finally {
            setAlinhando(null);
        }
    };

    // Geometria da régua: escala, empilhamento e eixo saem de `reguaGeometria`, compartilhado com a
    // régua de comunicados — duas réguas com matemática própria divergiriam na primeira correção.
    const desenho = React.useMemo(() => {
        if (!regua?.range) return null;
        const escala = criarEscala(regua.range.from, regua.range.to);
        const dia = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
        const eixo = marcasDoEixo(escala, dia);
        const lojas = (regua.stores || []).map((loja) => ({
            ...loja,
            turmas: (loja.cohorts || []).map((t) => {
                const posicionar = (f) => ({
                    ...f,
                    left: escala.pct(f.from, escala.t0),
                    right: escala.pct(f.to, escala.t1),
                    semFim: !f.to,
                });
                // A régua mostra o que VAI acontecer: o real é a linha principal. Uma linha por
                // trilha distinta de quem está na turma (perene tem uma por data de entrada; três
                // bastam, o resto vira "+N"), depois o histórico de quem saiu.
                const ativas = (t.effective || []).filter((r) => !r.left);
                const saidas = (t.effective || []).filter((r) => r.left);
                const ativos = t.active_members ?? t.membros ?? 0;
                const reais = ativas.slice(0, MAX_TRILHAS_REAIS);
                // O planejado só aparece quando diz algo que o real não diz: quando diverge, ou
                // quando a turma ainda não teve ninguém (aí ele é o que vai acontecer). Turma
                // esvaziada NÃO desenha o planejado — nada dele vai acontecer.
                const mostrarPlanejado = (ativos > 0 && t.diverges)
                    || (ativos === 0 && saidas.length === 0 && !(t.members_ever > 0));
                return {
                    ...t,
                    ativos,
                    semAtivos: ativos === 0,
                    mostrarPlanejado,
                    // empilha porque duas fases nunca se sobrepõem, mas duas TURMAS da mesma loja sim
                    faixas: mostrarPlanejado ? empilharFaixas((t.planned || []).map(posicionar)) : [],
                    reais: reais.map((r) => ({ ...r, barras: (r.phases || []).map(posicionar) })),
                    saidas: saidas.slice(0, MAX_TRILHAS_REAIS).map((r) => ({ ...r, barras: (r.phases || []).map(posicionar) })),
                    trilhasOcultas: Math.max(0, ativas.length - reais.length),
                };
            }),
        }));
        return { escala, eixo, lojas, hoje: posicaoDeHoje(escala) };
    }, [regua]);

    const programaSelecionado = programs.find((p) => String(p.id) === String(form.program_id));
    const totalDias = (programaSelecionado?.phases || []).reduce((t, f) => t + (f.duration_days || 0), 0);

    return (
        <div className="turmas-page">
            {toast && <div className={`turmas-toast ${toast.type}`}>{toast.msg}</div>}

            <header className="turmas-header">
                <div>
                    <h1>Turmas</h1>
                    <p className="turmas-sub">
                        A turma é o que dá <strong>data</strong> ao programa. A janela de cada fase é
                        contada por membro, a partir da âncora — e nunca antes da admissão dele.
                    </p>
                </div>
                <div className="turmas-header-acoes">
                    <div className="turmas-visoes" role="tablist">
                        {[['lista', 'Lista'], ['regua', 'Régua']].map(([v, rotulo]) => (
                            <button key={v} role="tab" aria-selected={visao === v}
                                className={`turmas-visao${visao === v ? ' ativa' : ''}`}
                                onClick={() => setVisao(v)}>{rotulo}</button>
                        ))}
                    </div>
                    <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        {companies.map((c) => (
                            <option key={c.subdomain} value={c.subdomain}>{c.name || c.subdomain}</option>
                        ))}
                    </select>
                    <button className="turmas-btn-primary" onClick={() => setFormOpen((v) => !v)}>
                        {formOpen ? 'Cancelar' : '➕ Nova turma'}
                    </button>
                </div>
            </header>

            {formOpen && (
                <div className="turmas-form">
                    <div className="turmas-form-grid">
                        <div className="turmas-field">
                            <label>Programa *</label>
                            <select value={form.program_id} onChange={(e) => setForm((f) => ({ ...f, program_id: e.target.value }))}>
                                <option value="">— Selecione —</option>
                                {programs.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} ({(p.phases || []).length} fases)
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="turmas-field">
                            <label>Loja de origem *</label>
                            <select value={form.store_id} onChange={(e) => setForm((f) => ({ ...f, store_id: e.target.value }))}>
                                <option value="">— Selecione —</option>
                                {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                            </select>
                        </div>
                        <div className="turmas-field">
                            <label>Nome *</label>
                            <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                                placeholder="ex.: Morumbi Cerâmica — Turma 1" />
                        </div>
                        <div className="turmas-field">
                            <label>Início *</label>
                            <input type="date" value={form.date_start}
                                onChange={(e) => setForm((f) => ({ ...f, date_start: e.target.value }))} />
                        </div>
                        <div className="turmas-field">
                            <label>Ancoragem *</label>
                            <select value={form.anchor} onChange={(e) => setForm((f) => ({ ...f, anchor: e.target.value }))}>
                                <option value="cohort">Coorte fechada — todos começam na data da turma</option>
                                <option value="member">Turma perene — cada um começa quando entra</option>
                            </select>
                        </div>
                    </div>

                    <p className="turmas-nota">
                        {form.anchor === 'cohort'
                            ? 'Coorte fechada: a data da turma vale para todos. Quem for admitido depois dela começa na admissão, não antes.'
                            : 'Turma perene: cada membro tem a própria trilha, contada da entrada dele. Serve para o CS criar a turma uma vez e só ir adicionando gente.'}
                        {programaSelecionado && totalDias > 0 && (
                            <> O programa selecionado dura <strong>{totalDias} dias</strong> por membro.</>
                        )}
                    </p>

                    <div className="turmas-form-acoes">
                        <button className="turmas-btn-primary" onClick={salvar} disabled={saving}>
                            {saving ? 'Salvando…' : 'Criar turma'}
                        </button>
                    </div>
                </div>
            )}

            {visao === 'regua' && (
                <div className="turmas-regua">
                    <div className="turmas-regua-head">
                        <h2>Régua de fases por loja</h2>
                        <span className="turmas-regua-meta">
                            {carregandoRegua ? 'carregando…' : desenho
                                ? `${desenho.lojas.filter((l) => !l.sem_turma).length} de ${desenho.lojas.length} lojas com programa · ${brDate(regua.range.from)} a ${brDate(regua.range.to)}`
                                : '—'}
                        </span>
                    </div>

                    {regua?.sem_fundacao && (
                        <p className="turmas-vazio">Esta empresa ainda não tem a fundação de fases aplicada.</p>
                    )}

                    {desenho && (
                        <>
                            <div className="turmas-eixo">
                                {desenho.eixo.marcas.map((m, i) => (
                                    <span key={i} className="turmas-tick" style={{ left: `${m.left}%` }}>{m.label}</span>
                                ))}
                            </div>

                            <div className="turmas-raias">
                                {desenho.hoje !== null && (
                                    <div className="turmas-hoje" style={{ left: `calc(var(--label-w) + (100% - var(--label-w)) * ${desenho.hoje / 100})` }}>
                                        <span>hoje</span>
                                    </div>
                                )}

                                {desenho.lojas.map((loja) => (
                                    <div key={loja.store_id} className={`turmas-raia${loja.sem_turma ? ' vazia' : ''}`}>
                                        <span className="turmas-raia-label" title={loja.store_name}>{loja.store_name}</span>
                                        <div className="turmas-raia-trilhos">
                                            {/* Loja sem turma NÃO é buraco: é a resposta de "quem ainda não tem programa". */}
                                            {loja.sem_turma && <span className="turmas-sem-turma">sem programa no período</span>}

                                            {loja.turmas.map((t) => (
                                                <div key={t.cohort_id} className="turmas-turma">
                                                    <div className="turmas-turma-head">
                                                        <button className="turmas-turma-nome"
                                                            onClick={() => navigate(comEmpresa(`/turmas/${t.cohort_id}`, subdomain))}
                                                            title={`${t.program_name} · ${t.membros} membro(s) · âncora ${t.anchor === 'cohort' ? 'da turma' : 'do membro'}`}>
                                                            {t.name}
                                                        </button>
                                                        {t.today && (t.today.phases || []).length > 0 && (
                                                            <span className="turmas-hoje-fase" title="Em que fase os vendedores desta turma estão hoje (horário de Brasília), pela trilha real">
                                                                {t.today.phases.length === 1
                                                                    ? `Fase atual: ${t.today.phases[0].name} · ${t.today.phases[0].members} vendedor${t.today.phases[0].members === 1 ? '' : 'es'}`
                                                                    : `Fase atual: ${t.today.phases.map((p) => `${p.members} em ${p.name}`).join(' · ')}`}
                                                            </span>
                                                        )}
                                                        {t.diverges && (
                                                            <span className="turmas-diverge" title="A trilha real de algum membro difere do planejado da turma">
                                                                real ≠ planejado
                                                            </span>
                                                        )}
                                                        {t.semAtivos && (t.saidas.length > 0 || t.members_ever > 0) && (
                                                            <span className="turmas-diverge vazia" title="Todos os membros saíram desta turma: nada do planejado dela vai acontecer">
                                                                sem membros ativos
                                                            </span>
                                                        )}
                                                        {t.diverges && t.ativos > 0 && t.anchor === 'cohort' && (
                                                            <button className="turmas-alinhar" disabled={alinhando === t.cohort_id}
                                                                onClick={() => alinharPlano(t)}
                                                                title="Faz o planejado desta turma passar a ser o que os membros estão vivendo. Nenhuma data é alterada.">
                                                                {alinhando === t.cohort_id ? 'calculando…' : 'Alinhar planejado ao real'}
                                                            </button>
                                                        )}
                                                    </div>
                                                    {t.reais.map((r, ri) => (
                                                        <React.Fragment key={`real-${ri}`}>
                                                            <span className="turmas-linha-rot real">
                                                                real · {r.members} membro{r.members === 1 ? '' : 's'}
                                                                {r.matches_planned ? ' · igual ao planejado' : ''}
                                                            </span>
                                                            <div className="turmas-trilho">
                                                                {r.barras.map((f) => barraDeFase(f, 'real', `Real · ${r.members} membro(s)`))}
                                                            </div>
                                                        </React.Fragment>
                                                    ))}
                                                    {t.saidas.map((r, ri) => (
                                                        <React.Fragment key={`saiu-${ri}`}>
                                                            <span className="turmas-linha-rot saiu">
                                                                histórico · {r.members} membro{r.members === 1 ? '' : 's'} que saí{r.members === 1 ? 'u' : 'ram'}{r.left_on ? ` em ${brDate(r.left_on)}` : ''}
                                                            </span>
                                                            <div className="turmas-trilho">
                                                                {r.barras.map((f) => barraDeFase(f, 'saiu', `Histórico · ${r.members} membro(s) que saíram`))}
                                                            </div>
                                                        </React.Fragment>
                                                    ))}
                                                    {t.mostrarPlanejado && (
                                                        <span className="turmas-linha-rot">
                                                            planejado{t.plan_custom ? ' (da turma)' : ''}
                                                            {t.ativos === 0 ? ' · turma ainda sem membros' : ' · difere do real'}
                                                        </span>
                                                    )}
                                                    {t.faixas.map((faixa, fi) => (
                                                        <div key={fi} className="turmas-trilho">
                                                            {faixa.map((f) => barraDeFase(f, 'planejada', 'Planejado'))}
                                                        </div>
                                                    ))}
                                                    {t.trilhasOcultas > 0 && (
                                                        <span className="turmas-linha-rot real">
                                                            +{t.trilhasOcultas} trilha(s) real(is) diferente(s) — membros que entraram em datas diferentes
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <p className="turmas-regua-foot">
                                As barras verdes são o <strong>real</strong>: a trilha de quem está na turma, o que vai
                                acontecer e o que vale para o app e a pontuação. Em cinza, o <strong>histórico</strong> de
                                quem saiu, até o dia da saída. O <strong>planejado</strong> (início da turma + duração das
                                fases) só aparece quando difere do real ou quando a turma ainda não tem ninguém. Quando o CS
                                força ou pula uma fase, o planejado da turma acompanha sozinho; “Alinhar planejado ao real”
                                resolve as turmas ajustadas antes dessa regra, sem mexer em nenhuma data. 🔒 marca fase que
                                tira do ranking.
                            </p>
                        </>
                    )}
                </div>
            )}

            <div className="turmas-lista" hidden={visao !== 'lista'}>
                <h2>Turmas {loading ? '(carregando…)' : `(${cohorts.length})`}</h2>
                {!loading && !cohorts.length && <p className="turmas-vazio">Nenhuma turma nesta empresa.</p>}
                {cohorts.length > 0 && (
                    <table className="turmas-table">
                        <thead>
                            <tr>
                                <th>Turma</th><th>Programa</th><th>Loja de origem</th>
                                <th>Início</th><th>Ancoragem</th><th>Membros</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {cohorts.map((c) => (
                                <tr key={c.id}>
                                    <td className="turmas-nome">{c.name}</td>
                                    <td>{c.program?.name}</td>
                                    <td>{c.store?.name || '—'}</td>
                                    <td>{brDate(c.date_start)}</td>
                                    <td>{c.anchor === 'member' ? 'Perene' : 'Coorte fechada'}</td>
                                    <td>
                                        {c.members}
                                        {c.members_in_other_store > 0 && (
                                            <span className="turmas-tag"
                                                title="Estes membros trocaram de loja e continuam na turma: a fase é do programa, não da loja. Mover o vínculo reiniciaria a trilha deles.">
                                                {c.members_in_other_store} em outra loja
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <button className="turmas-btn" onClick={() => navigate(comEmpresa(`/turmas/${c.id}`, subdomain))}>
                                            Abrir
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default Turmas;
